/**
 * Admin Academy Courses Page - List all courses with management actions.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconPlus,
  IconAlertTriangle,
  IconAcademicCap,
  IconCheck,
  IconUsers,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { useAdminCourses } from "./hooks";
import type { AdminAcademyCourse } from "./hooks";

// =============================================================================
// COURSE CARD
// =============================================================================

interface CourseCardProps {
  course: AdminAcademyCourse;
  onClick: () => void;
}

function CourseCard({ course, onClick }: CourseCardProps) {
  const questionsCount = course.exam?.questionsCount ?? 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-white border border-slate-100 shadow-sm active:scale-[0.99] transition-transform"
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            course.isActive
              ? "bg-blue-100 text-blue-600"
              : "bg-slate-100 text-slate-400"
          }`}
        >
          <IconAcademicCap className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-[15px] font-semibold text-slate-900 truncate">
              {course.title}
            </h3>
            {!course.isActive && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 rounded">
                Скрыт
              </span>
            )}
          </div>
          {course.description && (
            <p className="text-[13px] text-slate-500 line-clamp-1 mb-2">
              {course.description}
            </p>
          )}
          <div className="flex items-center gap-3 text-[12px] text-slate-400">
            {course.exam ? (
              <>
                <span className="flex items-center gap-1">
                  <IconCheck className="w-3.5 h-3.5" />
                  {questionsCount} вопр.
                </span>
              </>
            ) : (
              <span className="text-amber-500">Нет теста</span>
            )}
            {course.externalId && (
              <>
                <span className="text-slate-300">·</span>
                <span>UUID: {course.externalId.slice(0, 8)}...</span>
              </>
            )}
          </div>
        </div>
        <span className="text-[13px] text-slate-400 flex-shrink-0">→</span>
      </div>
    </button>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export function AdminCoursesPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { courses, isLoading, error, refetch } = useAdminCourses();

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (isLoading && courses.length === 0) {
    return <PageLoader message="Загрузка курсов..." />;
  }

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            {getErrorMessage(error, "Не удалось загрузить курсы")}
          </p>
          <button onClick={refetch} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  const activeCount = courses.filter((c) => c.isActive).length;
  const sortedCourses = [...courses].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
          <IconAcademicCap className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">
            Библейская академия
          </h1>
          <p className="text-[13px] text-slate-500">
            {activeCount} активных из {courses.length}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            navigate("/admin/academy/new");
          }}
          className="w-10 h-10 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Создать курс"
        >
          <IconPlus className="w-5 h-5" />
        </button>
      </div>

      {/* Quick actions */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            haptic("light");
            navigate("/admin/academy/analytics");
          }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-medium active:scale-95 transition-transform"
        >
          <IconUsers className="w-4 h-4" />
          Аналитика
        </button>
      </div>

      {/* Empty state */}
      {courses.length === 0 && (
        <div className="card p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 flex items-center justify-center">
            <IconAcademicCap className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900 mb-2">
            Нет курсов
          </h3>
          <p className="text-[13px] text-slate-500 mb-4">
            Создайте первый курс для академии
          </p>
          <button
            onClick={() => {
              haptic("light");
              navigate("/admin/academy/new");
            }}
            className="btn-primary"
          >
            <IconPlus className="w-4 h-4 mr-2" />
            Создать курс
          </button>
        </div>
      )}

      {/* Course list */}
      {courses.length > 0 && (
        <div className="space-y-3">
          {sortedCourses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              onClick={() => {
                haptic("light");
                navigate(`/admin/academy/${course.id}`);
              }}
            />
          ))}
        </div>
      )}

      <div className="h-4" />
    </Layout>
  );
}
